import { PropsWithChildren, ReactNode } from 'react';

import dayjs from 'dayjs';

import { DATE } from '@cast/constants';
import {
  Alert,
  Icons,
  Switch,
  SxProps,
  Tooltip,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import {
  PremiumFeaturePage,
  TryPremiumFeature,
  UnblockPremiumFeaturePage,
} from '@cast/shared-trial-ui';
import { NoDataReason } from '@cast/types';

import { PageHeaderWithClusterWarnings } from 'features/cluster/_components/PageHeaderWithClusterWarnings';
import { LIST_PRICE_COLLECTION_START_DATE } from 'features/cost-report/constants';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { PageHeader } from 'main-layout/PageHeader';

import { CostReportControls } from './_components/CostReportControls';
import { NoData } from './_components/NoData';
import { UnblockPremiumFeature } from '../../../../components/trial/UnblockPremiumFeature';
import { CostReportTab, NoDataType } from '../../types/common';
import {
  CostReportTabs,
  CostReportTabsProps,
  defaultTabs,
} from '../CostReportTabs';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { getDocsLink } from '@cast/utils';

type Props = PropsWithChildren<
  {
    title: ReactNode;
    errorMessage: string;
    loadingMessage?: ReactNode;
    hasError?: boolean;
    refetch?: () => void;
    sx?: SxProps;
    showControls?: boolean;
    showNoData?: boolean;
    noDataReason?: NoDataReason;
    noPriceTypeSelector?: boolean;
    hideIdleCosts?: boolean;
    noDataType?: NoDataType;
    noDataComponent?: ReactNode;
  } & Partial<CostReportTabsProps>
>;

export const CostReportPageContainer = ({
  title,
  errorMessage,
  loadingMessage,
  children,
  hasError,
  refetch,
  sx,
  showControls = true,
  showNoData = false,
  noDataReason,
  noDataType = NoDataType.GENERAL,
  noDataComponent,
  noPriceTypeSelector,
  enabledTabs = [],
  currentTab,
  tabs = defaultTabs,
  hideIdleCosts,
}: Props) => {
  const { cluster } = useCluster();
  const { includeIdleCosts, updateIdleCosts, hasIncompleteListPriceData } =
    useCostReportContext();
  const isClusterLevel = !!cluster.id;

  let content = children;
  if (hasError) {
    content = (
      <Box mt="63px">
        <FailedToLoad title={errorMessage} refresh={refetch} />
      </Box>
    );
  } else if (showNoData || noDataReason) {
    content = noDataComponent ?? <NoData dataType={noDataType} />;
  }

  const PageHeaderComponent = isClusterLevel
    ? PageHeaderWithClusterWarnings
    : PageHeader;

  const showIdleCosts =
    (currentTab === CostReportTab.COMPUTE_COST ||
      currentTab === CostReportTab.RELIABILITY) &&
    !hideIdleCosts;

  return (
    <>
      <PageHeaderComponent heading={title}>
        {enabledTabs.length === 0 && (
          <CostReportControls noPriceTypeSelector={noPriceTypeSelector} />
        )}
      </PageHeaderComponent>

      <Stack gap={16} sx={sx}>
        {showControls && enabledTabs.length > 0 && (
          <Stack
            direction="row"
            alignItems="flex-end"
            gap={24}
            width="100%"
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <Box flexGrow={1}>
              <CostReportTabs
                enabledTabs={enabledTabs}
                tabs={tabs}
                currentTab={currentTab}
              />
            </Box>

            <CostReportControls noPriceTypeSelector={noPriceTypeSelector} />
          </Stack>
        )}
        {showIdleCosts && (
          <Stack
            direction="row"
            width="100%"
            flexWrap="wrap"
            justifyContent="flex-end"
            gap={8}
          >
            <Tooltip
              placement="top-end"
              title="Idle costs represent allocated but unused compute resources. When included, totals show allocated spend rather than just active usage."
            >
              <Stack direction="row" gap={4} alignItems="center">
                <Typography variant="P12M">Include idle costs</Typography>
                <Icons.Info size={14} />
              </Stack>
            </Tooltip>
            <Switch
              testId="idle-costs-switch"
              size="md"
              checked={includeIdleCosts}
              onChange={() => updateIdleCosts(!includeIdleCosts)}
            />
          </Stack>
        )}

        {hasIncompleteListPriceData && (
          <Alert color="info" testId="list-price-collection-alert">
            List price collection began on{' '}
            {dayjs(LIST_PRICE_COLLECTION_START_DATE).format(DATE)}. Earlier
            periods may show incomplete data.
          </Alert>
        )}

        {!hasError && loadingMessage}

        {currentTab &&
        [CostReportTab.NETWORK_COST, CostReportTab.GPU_UTILIZATION].includes(
          currentTab
        ) ? (
          <PremiumFeaturePage
            page={
              <UnblockPremiumFeaturePage
                paywall={
                  <UnblockPremiumFeature
                    name={
                      currentTab === CostReportTab.NETWORK_COST
                        ? 'Network cost monitoring'
                        : 'GPU utilization monitoring'
                    }
                    description={
                      currentTab === CostReportTab.NETWORK_COST
                        ? 'Gain complete visibility into the network costs of your workloads and identify optimization opportunities.'
                        : 'Monitor and optimize your GPU workload costs with advanced utilization insights.'
                    }
                    docs={{
                      link:
                        currentTab === CostReportTab.NETWORK_COST
                          ? getDocsLink('docs_network_cost')
                          : getDocsLink('docs_gpu_utilization'),
                    }}
                  />
                }
                page={content}
              />
            }
            paywall={
              <TryPremiumFeature
                name={tabs?.[currentTab]?.label ?? ''}
                docs={
                  currentTab === CostReportTab.NETWORK_COST
                    ? {
                        link: getDocsLink('docs_network_cost'),
                      }
                    : {
                        link: getDocsLink('docs_gpu_utilization'),
                      }
                }
              />
            }
          />
        ) : (
          content
        )}
      </Stack>
    </>
  );
};
